// src/features/chat/components/SenderMeta.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../../../components/avatars';
import { isAssistantMessage } from '../models/chatModel';
import senderMetaStyles from './SenderMeta.module.css';

/**
 * Derive sender display info from a message and cached profiles.
 *
 * @param {Object} message - Normalized message
 * @param {Object} senderProfiles - Map of userId -> profile
 * @param {string} fallbackName - Default name when no profile is found
 * @param {string} botName - Display name for assistant/bot messages
 * @returns {{ displayName: string|null, avatarUrl: string|null, isAssistant: boolean, profileHref: string|null }}
 */
export function getSenderDisplay(message, senderProfiles, fallbackName = 'User', botName = 'Assistant') {
  if (!message) {
    return { displayName: fallbackName, avatarUrl: null, isAssistant: false, profileHref: null };
  }

  const isAssistant = isAssistantMessage(message);
  if (isAssistant) {
    return { displayName: botName, avatarUrl: null, isAssistant: true, profileHref: null };
  }

  const profile = senderProfiles?.[message.senderId];
  const displayName = profile?.displayName || fallbackName;
  const avatarUrl = profile?.profilePictureUrl || profile?.avatarUrl || null;
  const profileHref = message.senderId ? `/profile/${message.senderId}` : null;

  return { displayName, avatarUrl, isAssistant: false, profileHref };
}

/**
 * Consistent sender avatar + name block used across chat views.
 */
export function SenderMeta({
  sender,
  botIcon,
  botName = 'Assistant',
  showAvatar = true,
  showName = true,
  linkToProfile,
  avatarProps = {},
}) {
  if (!sender) return null;

  const { displayName, avatarUrl, isAssistant } = sender;
  const resolvedName = isAssistant ? botName : displayName || botName;
  const profileLink = linkToProfile || sender.profileHref;
  const { className: avatarClassName, ...restAvatarProps } = avatarProps;
  const mergedAvatarClass = [senderMetaStyles.avatar, avatarClassName]
    .filter(Boolean)
    .join(' ');

  const avatarNode = isAssistant ? (
    <img
      src={botIcon || avatarUrl || undefined}
      alt={resolvedName}
      className={senderMetaStyles.avatar}
    />
  ) : (
    <Avatar
      name={resolvedName}
      src={avatarUrl || undefined}
      className={mergedAvatarClass}
      size="var(--chat-avatar-size)"
      {...restAvatarProps}
    />
  );

  const avatarWithLink =
    !isAssistant && profileLink ? (
      <Link to={profileLink} aria-label={`${resolvedName} profile`}>
        {avatarNode}
      </Link>
    ) : (
      avatarNode
    );

  return (
    <>
      {showAvatar && (
        <div className={senderMetaStyles.avatarContainer}>{avatarWithLink}</div>
      )}
      {showName && <div className={senderMetaStyles.senderName}>{resolvedName}</div>}
    </>
  );
}

export default SenderMeta;
